import moment, { Moment } from "moment";

/**
 * OFF-CYCLE QUARTERLY BILLING (frontend)
 *
 * Integrated Partners has one advisor (one repCode) who bills quarterly on a
 * non-standard cycle: Feb/May/Aug/Nov instead of Jan/Apr/Jul/Oct.
 *
 * Those contracts carry the schedule value "Quarterly 2" — everything else
 * about the billing JSON is unchanged:
 *
 *   { "schedule": "Quarterly 2", "type": "advance", "values": "Latest" }
 *
 * The anchor months are a constant in code, so "Quarterly 2" is the single gate.
 * Standard contracts are untouched: their helpers return exactly
 * `moment().startOf('quarter')` / `moment().endOf('quarter')`.
 *
 * This mirrors the backend module `shared/off-cycle-billing.ts`.
 *
 * ---------------------------------------------------------------------------
 * REMOVAL CHECKLIST (safe to delete once the advisor leaves the firm)
 *
 *   1. Retag the contracts (see the backend module's checklist).
 *   2. Delete this file.
 *   3. Invoice modals and `firm/custodian/contracts.vue`: replace
 *      `startOfBillingQuarter` / `endOfBillingQuarter` with
 *      `moment().startOf('quarter')` / `'endOf'`, and drop the `isOffCycle`
 *      branches they guard.
 *   4. Remove the "Quarterly 2" filter option in `firm/custodian/contracts.vue`.
 * ---------------------------------------------------------------------------
 */

/** The schedule value that marks an off-cycle (Feb/May/Aug/Nov) contract. */
export const OFF_CYCLE_SCHEDULE = "Quarterly 2";

/** The months in which an off-cycle billing period starts. */
export const OFF_CYCLE_START_MONTHS = [2, 5, 8, 11];

/** True when this schedule value is the off-cycle quarterly cycle. */
export function isOffCycleSchedule(schedule?: string): boolean {
  return schedule === OFF_CYCLE_SCHEDULE;
}

/** True for any quarterly cycle — the standard one or the off-cycle one. */
export function isQuarterlySchedule(schedule?: string): boolean {
  return schedule === 'Quarterly' || isOffCycleSchedule(schedule);
}

/**
 * The anchor months for a billing schedule, or `undefined` when it bills on the
 * standard calendar quarters. This is the single gate for this feature:
 * everything below is an inert no-op for `undefined`.
 */
export function offCycleMonths(billing?: { schedule?: string }): number[] | undefined {
  return isOffCycleSchedule(billing && billing.schedule)
    ? OFF_CYCLE_START_MONTHS
    : undefined;
}

/**
 * First day of the billing period containing `date`.
 * With no anchor this is the first day of the calendar quarter.
 */
export function startOfBillingQuarter(date: Moment | Date, billing?: { schedule?: string }): Moment {
  const d = moment(date);
  const months = offCycleMonths(billing);

  // Standard quarters: identical to moment's own quarter start.
  if (!months) return d.clone().startOf("quarter");

  const currentMonth = d.month() + 1;

  // latest anchor month that has already started this calendar year
  const candidates = months.filter((m) => m <= currentMonth);
  const anchorMonth = candidates.length
    ? candidates[candidates.length - 1]
    : months[months.length - 1];

  const start = d.clone().month(anchorMonth - 1).startOf("month");

  // when no anchor has started yet this year, the period began last year
  if (!candidates.length) start.subtract(1, "year");

  return start;
}

/**
 * Last day of the billing period containing `date` (inclusive).
 * One quarter after the period start, minus a day — this also handles the
 * cross-year Nov–Jan period. With no anchor this is the end of the calendar
 * quarter.
 */
export function endOfBillingQuarter(date: Moment | Date, billing?: { schedule?: string }): Moment {
  if (!offCycleMonths(billing))
    return moment(date).clone().endOf("quarter");

  return startOfBillingQuarter(date, billing)
    .add(3, "months")
    .subtract(1, "day");
}
